import { ReactNode } from 'react';

import MuiLink, { LinkProps } from '@mui/material/Link';
import MuiOpenInNewIcon from '@mui/icons-material/OpenInNew';
import { styled } from '../styles';
import { Variant as TypographyVariant } from '../Typography';

type Variant = 'default' | 'small' | 'large';

export interface Props extends Omit<LinkProps, 'component' | 'variant'> {
  to?: string;
  variant?: Variant;
  component?: ReactNode;
}

const OpenInNewIcon = styled(MuiOpenInNewIcon)(({ theme: { palette, spacing } }) => ({
  color: palette.icon.minor,
  fontSize: '1rem',
  marginLeft: spacing(0.5),
  position: 'relative',
  top: 4,
}));

const Link = styled(MuiLink, {
  shouldForwardProp: (prop) => !['shouldHaveUnderline'].includes(prop as string),
})<{ shouldHaveUnderline: boolean }>(({ shouldHaveUnderline }) => ({
  ...(shouldHaveUnderline
    ? {
        textDecoration: 'underline',
        '&:hover': {
          textDecoration: 'none',
        },
      }
    : {
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      }),
}));

const Hyperlink = ({
  children,
  variant = 'default',
  target,
  underline = 'always',
  sx = {},
  ...otherProps
}: Props) => {
  const typographyMapping: { [key in Variant]: TypographyVariant } = {
    default: 'hyperlink',
    small: 'hyperlinkSmall',
    large: 'hyperlinkLarge',
  };
  const shouldOpenInNewTab = target === '_blank';
  const shouldHaveUnderline = underline !== 'none' && variant !== 'large';
  const linkElement = (
    <Link
      shouldHaveUnderline={shouldHaveUnderline}
      variant={typographyMapping[variant]}
      sx={{ color: 'interaction.enabled', cursor: 'pointer', ...sx }}
      target={target}
      {...otherProps}
    >
      {children}
    </Link>
  );

  return shouldOpenInNewTab ? (
    <span>
      {linkElement}
      <OpenInNewIcon />
    </span>
  ) : (
    linkElement
  );
};

export default Hyperlink;
